<template>
  <v-app
    :class="[
      { 'nav-drawer-mini': menuIsVerticalNavMini },
      { 'content-full': appContentWidth === 'full' },
    ]"
    class="content-layout vertical-nav"
  >
    <v-navigation-drawer
      v-if="!menuIsMenuHidden"
      ref="layoutDrawer"
      v-model="isVerticalNavMenuActive"
      app
      :right="$vuetify.rtl"
      mini-variant-width="68"
      :expand-on-hover="menuIsVerticalNavMini"
      width="260"
      :color="menuIsVerticalNavMini ? (isDark ? '#201C30' : '#dfdee2') : null"
      class="app-navigation-menu"
    >
      <vertical-nav-menu
        :nav-menu-items="navMenuItems"
        @close-nav-menu="isVerticalNavMenuActive = false"
      />
    </v-navigation-drawer>

    <v-app-bar ref="refAppBar" app flat class="app-bar-static">
      <slot
        name="navbar"
        :scrollY="scrollY"
        :isVerticalNavMenuActive="isVerticalNavMenuActive"
        :toggleVerticalNavMenuActive="toggleVerticalNavMenuActive"
      ></slot>
    </v-app-bar>

    <slot name="v-app-content"></slot>

    <v-main>
      <app-content-container>
        <slot></slot>
      </app-content-container>
      <v-navigation-drawer
        ref="basketDrawer"
        v-model="$store.state.app.basketDrawer"
        right
        fixed
        temporary
        touchless
        floating
        stateless
        color="chip"
        :class="!$vuetify.breakpoint.mdAndDown ? 'rounded-l-xl' : null"
        :width="!$vuetify.breakpoint.mdAndDown ? '900px' : '100%'"
      >
        <slot name="basket"></slot>
      </v-navigation-drawer>
    </v-main>

    <v-overlay
      :value="$store.state.app.shallContentShowOverlay"
      z-index="4"
      absolute
      class="content-overlay"
    ></v-overlay>

    <v-snackbar
      v-model="$store.state.app.snackbar.show"
      :timeout="1500"
      text
      centered
      outlined
      :color="$store.state.app.snackbar.color"
      elevation="24"
    >
      <div class="text-body-2 text-center">
        {{ $store.state.app.snackbar.text }}
      </div>
    </v-snackbar>
  </v-app>
</template>

<script>
import { getVuetify } from "@/@core/utils";
import useAppConfig from "@core/@app-config/useAppConfig";
import AppContentContainer from "@core/layouts/components/app-content-container/AppContentContainer.vue";
import VerticalNavMenu from "@core/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";
import { ref, watch } from "@vue/composition-api";
import { useWindowScroll } from "@vueuse/core";

export default {
  components: {
    AppContentContainer,
    VerticalNavMenu,
  },
  props: {
    navMenuItems: {
      type: Array,
      required: true,
    },
  },
  setup() {
    /* eslint-disable object-curly-newline, operator-linebreak */
    const {
      menuIsVerticalNavMini,
      menuIsMenuHidden,
      isDark,
      appBarType,
      appBarIsBlurred,

      appContentWidth,
    } = useAppConfig();
    const $vuetify = getVuetify();
    const userData = JSON.parse(localStorage.getItem("userData"));
    const isVerticalNavMenuActive = ref(true);

    watch(
      () => $vuetify?.breakpoint.mdAndDown,
      (value) => {
        isVerticalNavMenuActive.value = !value;
      },
      {
        immediate: true,
      }
    );

    const toggleVerticalNavMenuActive = () => {
      isVerticalNavMenuActive.value = !isVerticalNavMenuActive.value;
    };

    const { y: scrollY } = useWindowScroll();

    return {
      isDark,
      userData,
      isVerticalNavMenuActive,
      toggleVerticalNavMenuActive,
      menuIsVerticalNavMini,
      menuIsMenuHidden,
      appBarType,
      appBarIsBlurred,

      appContentWidth,
      scrollY,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/layouts/styles/_variables";

.app-content-container {
  padding: $content-padding-vertical-navigation-menu;
}

@include theme(app-navigation-menu) using ($material) {
  background-color: map-deep-get($material, "background");
}

// Vuetify Fix
// https://github.com/vuetifyjs/vuetify/issues/13327
$nav-drawer-mini-width: 68px;

.v-application {
  .v-main {
    transition-duration: 0.3s;
  }

  &.nav-drawer-mini {
    .v-main {
      // We haven't used `ltr` & `rtl` mixin because those doesn't work with top-level selectors: https://github.com/vuetifyjs/vuetify/issues/13987
      @at-root {
        .nav-drawer-mini {
          &.v-application--is-ltr {
            .v-main {
              padding-left: $nav-drawer-mini-width !important;
              @media #{map-get($display-breakpoints, 'md-and-down')} {
                padding-left: 0px !important;
              }
            }
          }
          &.v-application--is-rtl {
            .v-main {
              padding-right: $nav-drawer-mini-width !important;
            }
          }
        }
      }
    }
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      .app-navigation-menu + .v-app-bar {
        left: $nav-drawer-mini-width !important;
      }
    }
  }

  .v-app-bar {
    max-width: calc(1440px - (1.5rem * 2));
    @media screen and (max-width: 1456px) {
      margin-left: 1.5rem !important;
      margin-right: 1.5rem !important;
    }

    @at-root .v-application {
      &.content-full {
        .v-app-bar {
          max-width: unset;
          margin-left: $content-padding-vertical-navigation-menu !important;
          margin-right: $content-padding-vertical-navigation-menu !important;
        }
      }

      &:not(.nav-drawer-mini) {
        @media screen and (max-width: 1263px) {
          .app-navigation-menu + .v-app-bar {
            margin-left: 0px !important;
            margin-right: 0px !important;
          }
        }
      }
      &.nav-drawer-mini {
        @media screen and (max-width: 1523px) {
          .v-app-bar {
            margin-left: 1.5rem !important;
            margin-right: 1.5rem !important;
          }
        }
      }
    }
  }

  .v-app-bar {
    //border-radius: 0 0 10px 10px !important;
    z-index: 5;

    &.v-toolbar:not(.app-bar-shinked) {
      background-color: #28243d;
    }

    &.app-bar-static {
      will-change: padding, background-color;
      transition: padding 0.2s ease, background-color 0.18s ease, left 0.3s ease;

      &.v-toolbar.v-sheet:not(.v-app-bar--is-scrolled) {
        box-shadow: none !important;
      }

      ::deep > .v-toolbar__content {
        padding: 0;
      }
    }
  }
}

@include theme(v-app-bar) using ($material) {
  &.v-toolbar.app-bar-shinked {
    background-color: map-deep-get($material, "cards");
  }
}

.v-application.content-layout {
  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .v-main,
    .v-app-bar {
      max-width: unset;
      left: 0 !important;
      @include ltr() {
        padding-left: 0 !important;
      }
      @include rtl() {
        padding-right: 0 !important;
      }
    }
  }
}
</style>
